import React, { useContext } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { Button, CardActions } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import ListsHeader from './ListsHeader';
import ListCarousel from './ListCarousel';
import ListsCompactList from './ListsCompactList';
import { NAVIGATION_URLS } from '../../constants/navigationUrls';
import { trackViewAllClick, trackListTileClick } from '../../utils/listsAnalytics';

const { arrayOf, bool, number, shape } = proptypes;

// Reuse the carousel's existing Voltron listeners, so no new listener is needed. Module-level so
// the reference is stable for ListCarousel's useMemo.
const handleTileClick = (_listId, position, source) => trackListTileClick(position, source);

const renderActionButtons = ({ isMobile, hideViewAllSuffix, onViewAllClick }) => (
  isMobile ? (
    <div className="sui-mt-auto">
      <CardActions>
        <Button
          fullWidth
          href={NAVIGATION_URLS.VIEW_ALL_LISTS}
          onClick={onViewAllClick}
          aria-label="View All Lists"
        >
          {hideViewAllSuffix ? 'View All' : 'View All Lists'}
        </Button>
      </CardActions>
    </div>
  ) : null
);

const ListsContainer = ({
  lists, maxMobileListSize, iconSize, componentId, isDashboardSnapshot, hideViewAllSuffix, fillHeight
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const headerActions = isMobile ? ['createNew'] : ['createNew', 'viewAll'];
  const viewAllLabel = `View All Lists (${lists.length.toLocaleString()})`;

  // Under fillHeight the lists take whatever height is left once View All has its own: through
  // these grid rows in Dashboard Snapshot, and through a flex column the carousel grows into
  // elsewhere.
  let gridTemplateRows;
  if (isDashboardSnapshot) {
    gridTemplateRows = fillHeight ? 'minmax(0, 1fr) auto' : 'minmax(0, auto)';
  }

  return (
    <div
      data-testid="lists-container"
      id="user-lists-v2"
      className={classNames('sui-@container sui-h-full', {
        'sui-grid': isDashboardSnapshot,
        'sui-flex sui-flex-col': fillHeight && !isDashboardSnapshot
      })}
      style={gridTemplateRows ? { gridTemplateRows } : undefined}
    >
      {!isDashboardSnapshot && (
        <ListsHeader
          actions={headerActions}
          listCount={lists.length}
          hideViewAllSuffix={hideViewAllSuffix}
          onViewAllClick={trackViewAllClick}
        />
      )}
      {isMobile ? (
        <div>
          <ListsCompactList
            lists={lists}
            iconSize={iconSize}
            maxMobileListSize={maxMobileListSize}
            isDashboardSnapshot={isDashboardSnapshot}
            onTileClick={handleTileClick}
          />
        </div>
      ) : (
        <div className={classNames('sui-min-w-0', {
          'sui-pt-6 sui-pb-6': isDashboardSnapshot,
          'sui-grow': fillHeight && !isDashboardSnapshot
        })}
        >
          <ListCarousel
            lists={lists}
            iconSize={iconSize}
            componentId={componentId}
            isDashboardSnapshot={isDashboardSnapshot}
            onTileClick={handleTileClick}
            fillHeight={fillHeight}
          />
        </div>
      )}
      {isDashboardSnapshot ? (
        <div data-testid="lists-snapshot-view-all" className="sui-pb-6">
          <div className="sui-block sm:sui-hidden">
            <Button href={NAVIGATION_URLS.VIEW_ALL_LISTS} onClick={trackViewAllClick} fullWidth>{viewAllLabel}</Button>
          </div>
          <div className="sui-hidden sm:sui-block">
            <div className="sui-flex sui-justify-end sui-pr-4">
              <Button href={NAVIGATION_URLS.VIEW_ALL_LISTS} onClick={trackViewAllClick} variant="secondary">
                {viewAllLabel}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        renderActionButtons({ isMobile, hideViewAllSuffix, onViewAllClick: trackViewAllClick })
      )}
    </div>
  );
};

ListsContainer.propTypes = {
  lists: arrayOf(shape({})),
  maxMobileListSize: number,
  iconSize: proptypes.string,
  componentId: proptypes.string,
  /**
   * See Lists.isDashboardSnapshot. View All still renders here, moved below the lists and
   * labeled with the list count, replacing renderActionButtons.
   */
  isDashboardSnapshot: bool,
  /** See Lists.hideViewAllSuffix. Applies to the header (desktop) and mobile View All only. */
  hideViewAllSuffix: bool,
  fillHeight: bool
};
ListsContainer.defaultProps = {
  lists: [],
  maxMobileListSize: 4,
  iconSize: 'small',
  componentId: undefined,
  isDashboardSnapshot: false,
  hideViewAllSuffix: false,
  fillHeight: false
};

export default ListsContainer;
